import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { publicOrderSchema, tableOrderSchema } from "@/lib/validation-schemas";
import { triggerProductionPrint } from "@/lib/public-order-printing";
import { logger } from "@/lib/logger";
import { logErrorToDatabase } from "@/hooks/useErrorLogger";
import { normalizeTableNumber } from "@/lib/utils";

export interface SelectedAddon {
  id: string;
  name: string;
  price: number;
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  notes?: string;
  image_url?: string | null;
  selectedAddons?: SelectedAddon[];
}

export interface ComboCartItem {
  comboId: string;
  comboName: string;
  comboType: string;
  price: number;
  quantity: number;
  notes?: string;
  image_url?: string | null;
  selection: {
    selectedFlavors?: Array<{ productId: string; productName: string; price: number }>;
    selectedChoices?: Array<{ comboItemId: string; choiceLabel: string; productId: string; productName: string }>;
  };
}

export interface OrderData {
  restaurantId: string;
  orderType: "delivery" | "pickup";
  customerName: string;
  customerPhone: string;
  deliveryAddress?: string;
  deliveryFee?: number;
  notes?: string;
  items: CartItem[];
  comboItems?: ComboCartItem[];
}

export interface TableOrderData {
  restaurantId: string;
  tableNumber: string;
  items: CartItem[];
  comboItems?: ComboCartItem[];
  source?: "tablet" | "qr"; // tablet = always auto-approve, qr = respect restaurant setting
}

export function usePublicOrder() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createOrder = async (data: OrderData): Promise<boolean> => {
    // Validate data with Zod
    const validation = publicOrderSchema.safeParse(data);
    
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      logger.warn("Public order validation failed:", validation.error.errors);
      return false;
    }

    if (data.items.length === 0) {
      toast.error("Adicione itens ao carrinho");
      return false;
    }

    setIsSubmitting(true);

    try {
      // Criar pedido
      const { data: orderId, error: orderError } = await supabase
        .rpc("create_public_order", {
          p_restaurant_id: data.restaurantId,
          p_order_type: data.orderType === "delivery" ? "tab" : "counter",
          p_customer_name: data.customerName.trim(),
          p_customer_phone: data.customerPhone.replace(/\D/g, ""), // Store only digits
          p_delivery_address: data.deliveryAddress?.trim() || null,
          p_delivery_fee: data.deliveryFee || 0,
          p_notes: data.notes?.trim() || null,
        });

      if (orderError) throw orderError;

      // Adicionar itens
      for (const item of data.items) {
        // Calculate total price including addons
        const addonsTotal = item.selectedAddons?.reduce((sum, addon) => sum + addon.price, 0) || 0;
        const itemTotalPrice = (item.price + addonsTotal) * item.quantity;
        
        const { error: itemError } = await supabase
          .rpc("add_public_order_item", {
            p_order_id: orderId,
            p_product_id: item.id,
            p_product_name: item.name,
            p_quantity: item.quantity,
            p_unit_price: item.price + addonsTotal,
            p_notes: item.notes?.trim() || null,
          });

        if (itemError) throw itemError;
      }

      // Send WhatsApp notification for order received (delivery orders)
      if (data.orderType === "delivery" && data.customerPhone) {
        supabase.functions.invoke('send-order-status-notification', {
          body: { order_id: orderId, notification_type: 'order_received' }
        }).catch(err => logger.warn("Failed to send order received notification:", err));
      }

      toast.success("Pedido enviado com sucesso!");
      return true;
    } catch (err) {
      logger.error("Erro ao criar pedido:", err);
      await logErrorToDatabase(err, "public_menu", data.restaurantId, {
        action: "createOrder",
        orderType: data.orderType,
        itemsCount: String(data.items.length),
      });
      toast.error("Erro ao enviar pedido. Tente novamente.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTableOrderItem = async (data: TableOrderData): Promise<boolean> => {
    const hasComboItems = data.comboItems && data.comboItems.length > 0;
    
    // Validate data with Zod (now supports combo-only orders)
    const validation = tableOrderSchema.safeParse({
      restaurantId: data.restaurantId,
      tableNumber: data.tableNumber,
      items: data.items,
      hasComboItems,
    });
    
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      logger.warn("Table order validation failed:", validation.error.errors);
      return false;
    }

    setIsSubmitting(true);

    // Normalize table number to ensure consistent matching (removes leading zeros)
    const normalizedTableNumber = normalizeTableNumber(data.tableNumber);

    try {
      // Tablet orders always auto-approve (they're inside the establishment)
      // QR code orders respect the restaurant's setting
      let autoApprove = false;
      
      if (data.source === "tablet") {
        autoApprove = true;
      } else {
        const { data: restaurant } = await supabase
          .from("restaurants")
          .select("auto_approve_table_orders")
          .eq("id", data.restaurantId)
          .single();
        autoApprove = (restaurant as any)?.auto_approve_table_orders ?? false;
      }

      for (const item of data.items) {
        // Calculate total price including addons
        const addonsTotal = item.selectedAddons?.reduce((sum, addon) => sum + addon.price, 0) || 0;
        
        const { error } = await supabase
          .rpc("add_table_order_item", {
            p_restaurant_id: data.restaurantId,
            p_table_number: normalizedTableNumber,
            p_product_id: item.id,
            p_product_name: item.name,
            p_quantity: item.quantity,
            p_unit_price: item.price + addonsTotal,
            p_notes: item.notes?.trim() || null,
            p_source: data.source || "qr",
            p_selected_addons: item.selectedAddons && item.selectedAddons.length > 0 
              ? item.selectedAddons 
              : null,
          });

        if (error) throw error;
      }

      // Add combo items
      if (data.comboItems && data.comboItems.length > 0) {
        // Ensure order exists BEFORE inserting combos (fixes combo-only orders)
        const { data: orderId, error: ensureError } = await supabase.rpc("ensure_table_order", {
          p_restaurant_id: data.restaurantId,
          p_table_number: normalizedTableNumber,
          p_source: data.source || "qr",
        });

        if (ensureError) {
          logger.error("Error ensuring table order:", ensureError);
          throw ensureError;
        }

        for (const comboItem of data.comboItems) {
          const { error } = await supabase
            .from("order_items")
            .insert({
              order_id: orderId,
              combo_id: comboItem.comboId,
              product_name: comboItem.comboName,
              quantity: comboItem.quantity,
              unit_price: comboItem.price,
              total_price: comboItem.price * comboItem.quantity,
              notes: comboItem.notes || null,
              combo_selections: comboItem.selection,
            });

          if (error) throw error;
        }

        // Update order totals after all combos inserted
        const { data: orderItems } = await supabase
          .from("order_items")
          .select("total_price")
          .eq("order_id", orderId);

        const newSubtotal = orderItems?.reduce((sum, i) => sum + Number(i.total_price), 0) || 0;

        // Update order totals and status (if autoApprove, set to preparing)
        await supabase
          .from("orders")
          .update({
            subtotal: newSubtotal,
            total: newSubtotal,
            status: autoApprove ? "preparing" : undefined,
            updated_at: new Date().toISOString(),
          })
          .eq("id", orderId);
      }

      // If auto-approve is enabled, trigger production print
      if (autoApprove) {
        // Get the table's current order to print
        const { data: table } = await supabase
          .from("tables")
          .select("current_order_id")
          .eq("restaurant_id", data.restaurantId)
          .eq("number", normalizedTableNumber)
          .single();

        if (table?.current_order_id) {
          await triggerProductionPrint(table.current_order_id, data.restaurantId);
        }
      }

      toast.success(autoApprove 
        ? "Pedido enviado para a cozinha!" 
        : "Pedido enviado! Aguardando aprovação."
      );
      return true;
    } catch (err) {
      logger.error("Erro ao adicionar item:", err);
      await logErrorToDatabase(err, data.source === "tablet" ? "tablet" : "public_menu", data.restaurantId, {
        action: "addTableOrderItem",
        tableNumber: data.tableNumber,
        itemsCount: String(data.items.length),
        comboItemsCount: String(data.comboItems?.length || 0),
      });
      toast.error("Erro ao enviar pedido. Tente novamente.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    createOrder,
    addTableOrderItem,
    isSubmitting,
  };
}
